import { AnimatePresence, motion } from 'motion/react';
import { Plus, Trash2, X } from 'lucide-react';
import { useState } from 'react';

export interface Budget {
  id: string;
  category: string;
  amount: number;
  uid: string;
}

interface BudgetModalProps {
  isOpen: boolean;
  onClose: () => void;
  budgets: Budget[];
  onAddBudget: (budget: Omit<Budget, 'id' | 'uid'>) => Promise<void>;
  onDeleteBudget: (id: string) => Promise<void>;
  categories: string[];
  currency: string;
}

export function BudgetModal({
  isOpen,
  onClose,
  budgets,
  onAddBudget,
  onDeleteBudget,
  categories,
  currency,
}: BudgetModalProps) {
  const [newBudget, setNewBudget] = useState({ category: '', amount: '' });
  const formatMoney = (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(
      amount
    );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newBudget.category || !newBudget.amount) return;

    await onAddBudget({
      category: newBudget.category,
      amount: parseFloat(newBudget.amount) || 0,
    });
    setNewBudget({ category: '', amount: '' });
  };

  // Filter out categories that already have a budget
  const availableCategories = categories.filter(
    (cat) => !budgets.some((b) => b.category === cat)
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-zinc-900/60 backdrop-blur-sm"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative flex max-h-[80vh] w-full max-w-lg flex-col overflow-hidden rounded-[32px] border border-zinc-100 bg-white shadow-2xl transition-colors dark:border-zinc-900 dark:bg-black"
          >
            <div className="flex items-center justify-between border-b border-zinc-100 p-6 dark:border-zinc-900">
              <div>
                <h2 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">
                  Monthly Budgets
                </h2>
                <p className="text-xs font-medium text-zinc-500">
                  Set spending limits for categories
                </p>
              </div>
              <button
                onClick={onClose}
                className="rounded-full p-2 transition-colors hover:bg-zinc-100 dark:hover:bg-zinc-800"
              >
                <X className="h-5 w-5 text-zinc-400" />
              </button>
            </div>

            <div className="flex-1 space-y-6 overflow-y-auto p-6">
              <form
                onSubmit={handleSubmit}
                className="grid grid-cols-1 items-end gap-3 rounded-2xl border border-zinc-100 bg-zinc-50 p-4 sm:grid-cols-12 dark:border-zinc-900 dark:bg-zinc-950/50"
              >
                <div className="space-y-1.5 sm:col-span-7">
                  <label className="ml-1 text-[10px] font-bold tracking-widest text-zinc-400 uppercase">
                    Category
                  </label>
                  <select
                    required
                    className="w-full appearance-none rounded-xl border border-zinc-200 bg-white px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500 dark:border-zinc-900 dark:bg-zinc-950 dark:text-white"
                    value={newBudget.category}
                    onChange={(e) =>
                      setNewBudget({ ...newBudget, category: e.target.value })
                    }
                  >
                    <option value="" disabled>
                      Select Category
                    </option>
                    {availableCategories.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                    {!availableCategories.includes(newBudget.category) &&
                      newBudget.category && (
                        <option value={newBudget.category}>
                          {newBudget.category}
                        </option>
                      )}
                  </select>
                </div>
                <div className="space-y-1.5 sm:col-span-4">
                  <label className="ml-1 text-[10px] font-bold tracking-widest text-zinc-400 uppercase">
                    Limit
                  </label>
                  <input
                    type="number"
                    placeholder="0.00"
                    step="0.01"
                    required
                    className="w-full rounded-xl border border-zinc-200 bg-white px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-indigo-500 dark:border-zinc-900 dark:bg-zinc-950 dark:text-white"
                    value={newBudget.amount}
                    onChange={(e) =>
                      setNewBudget({ ...newBudget, amount: e.target.value })
                    }
                  />
                </div>
                <button
                  type="submit"
                  className="flex h-[42px] items-center justify-center rounded-xl bg-zinc-900 text-white transition-opacity hover:opacity-90 sm:col-span-1 dark:bg-zinc-100 dark:text-zinc-900"
                >
                  <Plus className="h-5 w-5" />
                </button>
              </form>

              <div className="space-y-2">
                {budgets.length === 0 ? (
                  <div className="py-12 text-center text-sm text-zinc-400">
                    No budgets set. Add one above!
                  </div>
                ) : (
                  budgets.map((budget) => (
                    <div
                      key={budget.id}
                      className="group flex items-center justify-between rounded-2xl border border-zinc-100 bg-white p-4 transition-colors hover:border-indigo-100 dark:border-zinc-900 dark:bg-zinc-950 dark:hover:border-indigo-900/50"
                    >
                      <div>
                        <p className="font-bold text-zinc-900 dark:text-zinc-100">
                          {budget.category}
                        </p>
                        <p className="mt-0.5 text-xs font-bold text-indigo-600 dark:text-indigo-400">
                          {formatMoney(budget.amount)}{' '}
                          <span className="font-medium text-zinc-400">
                            / month
                          </span>
                        </p>
                      </div>
                      <button
                        onClick={() => onDeleteBudget(budget.id)}
                        className="rounded-xl p-2 text-zinc-400 transition-colors hover:bg-rose-50 hover:text-rose-500 dark:hover:bg-rose-900/20"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
